import { Add } from '@mui/icons-material';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useTranslation } from 'react-i18next';
import { EventParticipantStatusEnum } from 'src/shared/models/enums/eventParticipantStatus.enum';
import { EventDetailExportParticipantButton } from './eventDetailExportParticipantButton';
import { EventParticipantFilter } from './eventParticipantFilter';
import { EventParticipantDataGrid } from './eventParticipantDataGrid';
import { EventParticipant } from '../models/eventParticipant';
import { EventAddParticipantModal } from './eventAddParticipantModal';

type EventDetailTabParticipantsProps = {
  participants: EventParticipant[];
  eventId: number;
  eventFee: number;
  onAction: () => void;
  useTickets: boolean;
};

export const EventDetailTabParticipants = (props: EventDetailTabParticipantsProps) => {
  const { participants, eventId, eventFee, onAction, useTickets } = props;
  const { t } = useTranslation();

  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<EventParticipantStatusEnum[]>();
  const [searchString, setSearchString] = useState<string>('');
  const [filteredParticipants, setFilteredParticipants] = useState<EventParticipant[]>([]);

  useEffect(() => {
    if (participants) {
      const normalizedSearchString = searchString.trim().toLowerCase();
      const filtered = participants.filter((item: EventParticipant) => {
        const hasSelectedStatus = selectedStatuses && selectedStatuses.length ? selectedStatuses.some((x) => item.status === x) : true;

        if (!hasSelectedStatus) {
          return false;
        }

        if (!normalizedSearchString) {
          return true;
        }

        const searchValues = [item.firstName, item.lastName, item.email, item.phone, item.memberNr?.toString()]
          .filter((x): x is string => !!x)
          .map((x) => x.toLowerCase());

        return searchValues.some((x) => x.includes(normalizedSearchString));
      });
      setFilteredParticipants(filtered);
    }
  }, [participants, searchString, selectedStatuses]);

  return (
    <>
      <GridWrapper>
        <Grid item xs={12}>
          <ContentSection
            header={t('event:participants')}
            button={
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                {filteredParticipants.length > 0 && <EventDetailExportParticipantButton participants={filteredParticipants} />}
                <Button
                  size='small'
                  variant='outlined'
                  endIcon={<Add fontSize='small' />}
                  onClick={() => {
                    setShowAddParticipantModal(true);
                  }}
                >
                  {t('common:button.add')}
                </Button>
              </Box>
            }
          >
            {participants.length > 0 ? (
              <Box sx={{ pt: 1 }}>
                <EventParticipantFilter
                  participants={participants}
                  selectedStatuses={selectedStatuses}
                  searchString={searchString}
                  onStatusChange={(x) => setSelectedStatuses(x)}
                  onSearchStringChange={setSearchString}
                />
                <EventParticipantDataGrid eventId={eventId} participants={filteredParticipants} />
              </Box>
            ) : (
              <Typography>{t('event:noParticipants')}</Typography>
            )}
          </ContentSection>
        </Grid>
      </GridWrapper>
      {eventId && showAddParticipantModal && (
        <EventAddParticipantModal
          modalOpen={showAddParticipantModal}
          onClose={() => setShowAddParticipantModal(false)}
          successCallback={() => {
            setShowAddParticipantModal(false);
            onAction();
          }}
          eventId={eventId}
          eventFee={eventFee}
          useTickets={useTickets}
        />
      )}
    </>
  );
};
